/**
 * External dependencies
 */
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Link, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

/**
 * Internal dependencies
 */
import BreadcrumbWrapper from '@Components/BreadcrumbWrapper';
import PurchaseReceipt from '@Components/PurchaseReceipt';
import { FishProfile } from '@Types/fish';
import { DeliveryAddress, Purchase } from '@Types/purchase';
import { useGetPurchase } from '../../../../hooks/usePurchases';
import { formatPhoneNumber } from '@Utils/utils';
import { getPaymentTypes } from '@Utils/toast/purchaseHelpers';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';

const getCustomerName = (purchase?: Purchase): string => {
  const profile = purchase.fish?.fishProfiles?.find(
    (profile: FishProfile) => profile.profileType === 'MEMBER',
  );

  if (!profile) {
    const firstProfile = purchase.fish?.fishProfiles?.[0];
    if (firstProfile) {
      return `${firstProfile.fName} ${firstProfile.lName}`;
    }
    return 'Unknown';
  }

  return `${profile.fName} ${profile.lName}`;
};

function formatAddress(address: DeliveryAddress): string[] {
  const lines: string[] = [];

  if (address.streetAddress1) {
    lines.push(address.streetAddress1);
  }

  if (address.streetAddress2) {
    lines.push(address.streetAddress2);
  }

  const cityStateZip: string[] = [];
  if (address.city) {
    cityStateZip.push(address.city);
  }
  if (address.state) {
    cityStateZip.push(address.state);
  }
  if (cityStateZip.length > 0) {
    let line = cityStateZip.join(', ');
    if (address.zipCode) {
      line += ` ${address.zipCode}`;
    }
    lines.push(line);
  } else if (address.zipCode) {
    lines.push(address.zipCode);
  }

  if (address.country) {
    lines.push(address.country);
  }

  return lines;
}

export default function OrderDetails() {
  const { orderId } = useParams();
  const { data: purchase, isLoading } = useGetPurchase(orderId!, {
    'include': 'fish.fishProfiles,redemptions.promoCode',
  });

  const customerName = useMemo(() => {
    if (!purchase) {
      return '';
    }

    return getCustomerName(purchase);
  }, [purchase]);

  const displayTitle = useMemo(() => {
    if (!purchase || !customerName) {
      return '';
    }

    return `${customerName} on ${DateTime.fromISO(
      purchase.createdAt,
    ).toLocaleString(DateTime.DATETIME_MED)}`;
  }, [purchase, customerName]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <BreadcrumbWrapper
        crumbArray={[
          { label: 'Orders', path: '/shop/orders' },
          { label: displayTitle, path: `/shop/orders/${orderId}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>{displayTitle}</Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">ORDER</p>
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        <div className="flex flex-col space-y-8 xl:col-span-2">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Details</Heading>
            </div>
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 gap-8">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Confirmation Number
                      </div>
                      <div>{purchase.confirmationNumber || '-'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Product Description
                      </div>
                      <div>{purchase.productDescription || '-'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Status
                      </div>
                      <Badge variant="secondary">{purchase.status}</Badge>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Payment Type
                      </div>
                      <div className="flex flex-row gap-2">
                        {getPaymentTypes(purchase.order?.checks || []).map(
                          (type) => (
                            <Badge key={`payment-type-${type}`}>{type}</Badge>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-4">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Created At
                      </div>
                      <div>
                        {purchase.createdAt
                          ? DateTime.fromISO(purchase.createdAt).toLocaleString(
                              DateTime.DATETIME_MED,
                            )
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Updated At
                      </div>
                      <div>
                        {purchase.updatedAt
                          ? DateTime.fromISO(purchase.updatedAt).toLocaleString(
                              DateTime.DATETIME_MED,
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {purchase.order?.checks?.[0] && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Receipt</Heading>
              </div>
              <PurchaseReceipt
                check={purchase.order.checks[0]}
                showPaymentTypes={false}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col space-y-8">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Customer</Heading>
            </div>
            <Card>
              <CardContent>
                {purchase.fishId ? (
                  <div className="flex flex-col space-y-2">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Name
                      </div>
                      <Link
                        to={`/guests/${purchase.fishId}`}
                        className="text-blue-600 hover:underline"
                      >
                        {customerName}
                      </Link>
                    </div>
                    {purchase.fish?.phoneNumber && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Phone
                        </div>
                        <div>
                          {formatPhoneNumber(
                            purchase.fish.phoneNumber.slice(2),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-zinc-500">
                    No customer associated
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {purchase.deliveryAddress && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Delivery Address</Heading>
              </div>
              <Card>
                <CardContent>
                  <div className="rounded-md bg-zinc-50 p-4">
                    {formatAddress(purchase.deliveryAddress).map(
                      (line, index) => (
                        <p key={index} className="text-sm text-zinc-700">
                          {line}
                        </p>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
